import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../lib/errors";
import { ImportUploadPanel } from "../components/imports/ImportUploadPanel";
import { ImportBatchList } from "../components/imports/ImportBatchList";
import { ImportBatchDetail } from "../components/imports/ImportBatchDetail";
import { downloadThroughApi } from "../components/imports/importShared";

/* LT-19b §11 — Materials → Imports. Upload panel on top, the ledger under
 * it, detail view at ?tab=imports&batch=<id>. */
export default function MaterialsImportsTab() {
  const [params, setParams] = useSearchParams();
  const batchId = params.get("batch");
  const [suppliers, setSuppliers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [totalAll, setTotalAll] = useState(0);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    api.get("/material-imports", { params: { include_archived: showArchived } })
      .then((r) => { setBatches(r.data.batches || []); setTotalAll(r.data.total_including_archived || 0); })
      .catch((e) => toast.error(formatApiError(e, "Failed to load imports")));
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/suppliers", { params: { include_archived: true } }).then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  const open = (b) => setParams({ tab: "imports", batch: b.id });
  const back = () => { setParams({ tab: "imports" }); load(); };
  const run = (fn, okMsg) => fn().then(() => { if (okMsg) toast.success(okMsg); load(); }).catch((e) => toast.error(formatApiError(e)));

  if (batchId) {
    return <ImportBatchDetail batchId={batchId} onBack={back} onChanged={load} />;
  }
  return (
    <div className="space-y-6" data-testid="materials-imports-tab">
      <ImportUploadPanel suppliers={suppliers} onUploaded={(r) => {
        const c = r.batch?.counts || {};
        toast.success(`Preview ready — ${c.create ?? 0} create, ${c.update ?? 0} update, ${c.unchanged ?? 0} unchanged, ${c.reject ?? 0} rejected`);
        if (r.duplicate_of) toast.warning(`This exact file was already applied on ${r.duplicate_of.applied_at?.slice(0, 10)} — re-running is allowed.`);
        open(r.batch);
      }} />
      <ImportBatchList batches={batches} totalIncludingArchived={totalAll} showArchived={showArchived} onToggleArchived={setShowArchived}
        onOpen={open}
        onDownload={(b) => run(() => downloadThroughApi(`/material-imports/${b.id}/file`, b.filename))}
        onDiscard={(b) => run(() => api.post(`/material-imports/${b.id}/discard`), "Draft discarded — file and rows kept")}
        onArchive={(b) => run(() => api.post(`/material-imports/${b.id}/archive`), "Archived — hidden from the default list, nothing deleted")}
        onUnarchive={(b) => run(() => api.post(`/material-imports/${b.id}/unarchive`), "Unarchived")} />
    </div>
  );
}
